import React, { Fragment } from 'react';
import type { DeepReadonly } from 'ts-essentials';
import Styles from './index.less';
import Tooltip from '@tencent/dui/esm/components/Tooltip';
import {
    DataType,
    ICTTableRow,
    ICTColorDmlA,
    ISlideDataModel,
} from '@tencent/slide-sdk-model';
import classnames from 'classnames';
import { ICTTableStyleDmlA } from '@tencent/slide-sdk-pb-model/src/ultrabuf/ultrabuf.types';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import { IPlaceholderShapeData } from '@tencent/slide-sdk-model/src/data';
import { ITableData } from '@tencent/slide-sdk-model/src/data/shape-data';
import { emuToAlpha } from '@src/slide/infrastructure/module/independent/unit';
import { alphaToOpacity } from '@framework/workbench/drawer/utils';
import { hexToRgba } from '@src/slide/infrastructure/module/independent/color/transform';
import { ColorConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/color/color-converter';
import { DeepPartial } from 'ts-essentials';
import { TDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';
import { IPageDataModel } from '@tencent/slide-sdk-model/src/model/page-data-model';

export interface ITableCardProps {
    slide: ISlideModel;
    id: string;
    name: string;
    tblStyle: ICTTableStyleDmlA | undefined;
    firstRow: boolean;
    lastRow: boolean;
    firstCol: boolean;
    lastCol: boolean;
    bandRow: boolean;
    bandCol: boolean;
    tableStyleData: ITableData;
    onClick();
}

interface ITableRow {
    tr: ICTTableRow;
    rowIndex: number;
}

interface ITableCell {
    tr: DeepReadonly<DeepPartial<ICTTableRow>>;
    rowIndex: number;
}

interface ITableBaseCell {
    backgroundColor: string;
    isWhiteLine: boolean;
    colIndex: number;
    tdColor: TDColor;
}

function getColorByTheme(colorDmlA: ICTColorDmlA, slide: ISlideDataModel): string {
    return SlideDataServices.SDKDataServices.ColorManager()
        ?.resolveColor(colorDmlA, slide)?.srgbClr?.val || '';
}

/**
 * 表格快速样式卡片
 */
export default class TableCard extends React.PureComponent<ITableCardProps> {
    public constructor(props: ITableCardProps) {
        super(props);
    }

    public render() {
        const { tableStyleData } = this.props;
        const background = this.getBackgroundColor(tableStyleData);
        return <Tooltip
            title={this.props.name}
            placement="bottom">
            <div
                onClick={this.props.onClick}
                style={{
                    background,
                }}
                className={classnames({
                    [Styles['table-card']]: true,
                })}>
                <div className={Styles['table-card-border']}>
                    {
                        this.getContentRow(tableStyleData)
                    }
                </div>
            </div>
        </Tooltip>;
    }

    public getContentRow(tableData: ITableData) {
        const trList = tableData.graphic?.graphicData?.tbl?.tr || [];
        return Array.from({ length: 5 }).map((item, index) => {
            const tc = trList[index].tc?.[0];
            let backgroundColor = '';
            if (tc?.tcPr?.solidFill?.egColorChoice) {
                const tdColor = ColorConverter.convertPartialDataFromSdkToUi(tc.tcPr.solidFill.egColorChoice);
                backgroundColor = tdColor.rgba;
            }

            let backgroundBorderColor: undefined | string = undefined;
            if (tc?.tcPr?.lnR?.egLineFillProperties?.solidFill?.egColorChoice) {
                const { egColorChoice } = tc.tcPr.lnR.egLineFillProperties.solidFill;
                const tdColor = ColorConverter.convertPartialDataFromSdkToUi(egColorChoice);
                backgroundBorderColor = tdColor.rgba;
            } else {
                backgroundBorderColor = backgroundColor;
            }


            return <Fragment
                key={index}>
                <TableBaseRow
                    tr={trList[index]}
                    rowIndex={index}
                    key={index}
                />
                {
                    index < 4 && <RowBorder backgroundBorderColor={backgroundBorderColor} />
                }
            </Fragment>;
        });
    }

    // 获取表格背景色
    public getBackgroundColor(tableData: ITableData): string {
        const tblPr = tableData.graphic?.graphicData?.tbl?.tblPr;
        if (tblPr) {
            const { gradFill, solidFill } = tblPr;
            if (gradFill?.gsLst?.gs?.length) {
                const pointArray = gradFill.gsLst.gs;
                if (pointArray.length) {
                    let gradientBg = 'linear-gradient(';
                    pointArray.forEach((item, index) => {
                        const { egColorChoice, pos = 0 } = item;
                        const bgColor = `#${egColorChoice?.srgbClr?.val || ''}`;
                        gradientBg += `${bgColor} ${pos / 1000}%`;
                        if (index < pointArray.length - 1) {
                            gradientBg += ',';
                        }
                    });
                    gradientBg += ')';

                    return gradientBg;
                }
            }

            if (solidFill?.egColorChoice?.srgbClr?.val) {
                return `#${solidFill.egColorChoice.srgbClr.val}`;
            }
        }

        return '';
    }
}

export class TableBaseRow extends React.PureComponent<ITableRow> {
    public render() {
        return <div className={Styles['table-base-row']}>
            <TableCellGroup
                {...this.props}
            />
        </div>;
    }
}

export class TableCellGroup extends React.PureComponent<ITableCell> {
    public render() {
        return <Fragment>
            {
                this.getContentRow()
            }
        </Fragment>;
    }

    public getContentRow() {
        const { tr, rowIndex } = this.props;
        const componentList = Array.from({ length: 5 });
        const TABLE_COL_NUMBER = 4;
        return componentList.map((item, index) => {
            const tc = tr.tc?.[index];
            let tdColor = new TDColor({
                rgbaStr: 'ffffff',
            });
            let backgroundColor = '';
            if (tc?.tcPr?.solidFill?.egColorChoice) {
                tdColor = ColorConverter.convertPartialDataFromSdkToUi(tc.tcPr.solidFill.egColorChoice);
                backgroundColor = tdColor.rgba;
            }

            let backgroundBorderColor: undefined | string = undefined;
            if (tc?.tcPr?.lnR?.egLineFillProperties?.solidFill?.egColorChoice) {
                const { egColorChoice } = tc.tcPr.lnR.egLineFillProperties.solidFill;
                const tdColor = ColorConverter.convertPartialDataFromSdkToUi(egColorChoice);
                backgroundBorderColor = tdColor.rgba;
            } else {
                backgroundBorderColor = backgroundColor;
            }

            return <Fragment
                key={index}>
                <TableBaseCell
                    key={index}
                    {...this.props}
                    isWhiteLine={rowIndex === 0 && !!backgroundColor}
                    backgroundColor={backgroundColor}
                    colIndex={index}
                    tdColor={tdColor}
                />
                {
                    index < TABLE_COL_NUMBER && <CellBorder
                        backgroundBorderColor={backgroundBorderColor}
                    />
                }
            </Fragment>;
        });
    }
}

interface ICellBorder {
    backgroundBorderColor?: string;
}

export class CellBorder extends React.PureComponent<ICellBorder> {
    public render() {
        return (
            <div style={{
                background: this.props.backgroundBorderColor || 'transparent',
                height: '100%',
                width: 1,
                flexShrink: 0,
            }} />
        );
    }
}

export class RowBorder extends React.PureComponent<ICellBorder> {
    public render() {
        return (
            <div style={{
                background: this.props.backgroundBorderColor || 'transparent',
                width: '100%',
                height: 1,
                flexShrink: 0,
            }} />
        );
    }
}

export class TableBaseCell extends React.PureComponent<ITableBaseCell> {
    public render() {
        let isWhiteLine = false;
        const { hsl } = this.props.tdColor;
        if (hsl.L < 60) {
            isWhiteLine = true;
        } else {
            isWhiteLine = false;
        }

        return <div
            style={{
                backgroundColor: `${this.props.backgroundColor}`,
            }}
            className={Styles['table-base-cell']}>
            <div className={Styles['table-cell-line']} style={{
                backgroundColor: isWhiteLine ? 'white' : 'black',
            }} />
        </div>;
    }
}


